import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { projectAPI, trainingDataAPI, jobAPI, modelAPI, apiKeyAPI, vendorAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Play, Loader2, CheckCircle2, XCircle, Clock, Ban,
  FileText, Cpu, RefreshCw, Copy, Trash2, Eye
} from 'lucide-react';
import JobDetailDialog from '../components/JobDetailDialog';

const statusIcons = {
  pending: <Clock className="w-4 h-4" />,
  queued: <Clock className="w-4 h-4" />,
  running: <Loader2 className="w-4 h-4 animate-spin" />,
  succeeded: <CheckCircle2 className="w-4 h-4 text-emerald-500" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
  cancelled: <Ban className="w-4 h-4 text-zinc-500" />,
};

const statusColors = {
  pending: 'warning',
  queued: 'warning',
  running: 'info',
  succeeded: 'success',
  failed: 'destructive',
  cancelled: 'secondary',
};

const progressValues = {
  pending: 5,
  queued: 15,
  running: 50,
  succeeded: 100,
  failed: 100,
  cancelled: 100,
};

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [trainingData, setTrainingData] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [models, setModels] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Upload dialog state
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadForm, setUploadForm] = useState({ name: '', apiKeyId: '', file: null });
  const [uploading, setUploading] = useState(false);

  // Job dialog state
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [jobForm, setJobForm] = useState({
    apiKeyId: '',
    trainingDataId: '',
    baseModel: '',
    name: '',
    nEpochs: 3,
  });
  const [creatingJob, setCreatingJob] = useState(false);
  
  // Job detail dialog state
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [showJobDetailDialog, setShowJobDetailDialog] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [projectRes, dataRes, jobsRes, modelsRes, keysRes, vendorsRes] = await Promise.all([
        projectAPI.get(projectId),
        trainingDataAPI.list(projectId),
        jobAPI.list(projectId),
        modelAPI.listByProject(projectId),
        apiKeyAPI.list(),
        vendorAPI.list(),
      ]);
      
      setProject(projectRes.data);
      setTrainingData(dataRes.data);
      setJobs(jobsRes.data);
      setModels(modelsRes.data);
      setApiKeys(keysRes.data);
      setVendors(vendorsRes.data);
    } catch (error) {
      toast.error('Failed to load project data');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  }, [projectId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh running jobs status
  useEffect(() => {
    const runningJobs = jobs.filter(j => ['pending', 'queued', 'running'].includes(j.status));
    if (runningJobs.length === 0) return;

    const interval = setInterval(async () => {
      for (const job of runningJobs) {
        try {
          const res = await jobAPI.get(job.id);
          setJobs(prev => prev.map(j => j.id === job.id ? res.data : j));
          
          // If job completed, reload models
          if (res.data.status === 'succeeded' && res.data.fine_tuned_model) {
            const modelsRes = await modelAPI.listByProject(projectId);
            setModels(modelsRes.data);
          }
        } catch (error) {
          console.error('Failed to refresh job status', error);
        }
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [jobs, projectId]);

  const handleUpload = async () => {
    if (!uploadForm.name || !uploadForm.apiKeyId || !uploadForm.file) {
      toast.error('Please fill all fields');
      return;
    }

    setUploading(true);
    try {
      await trainingDataAPI.upload(projectId, uploadForm.apiKeyId, uploadForm.name, uploadForm.file);
      toast.success('Training data uploaded');
      setShowUploadDialog(false);
      setUploadForm({ name: '', apiKeyId: '', file: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateJob = async () => {
    if (!jobForm.apiKeyId || !jobForm.trainingDataId || !jobForm.baseModel) {
      toast.error('Please fill all required fields');
      return;
    }

    setCreatingJob(true);
    try {
      await jobAPI.create(projectId, {
        api_key_id: jobForm.apiKeyId,
        training_data_id: jobForm.trainingDataId,
        base_model: jobForm.baseModel,
        name: jobForm.name,
        n_epochs: jobForm.nEpochs,
      });
      toast.success('Fine-tuning job created');
      setShowJobDialog(false);
      setJobForm({ apiKeyId: '', trainingDataId: '', baseModel: '', name: '', nEpochs: 3 });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job');
    } finally {
      setCreatingJob(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Cancel this job?')) return;
    
    try {
      await jobAPI.cancel(jobId);
      toast.success('Job cancelled');
      loadData();
    } catch (error) {
      toast.error('Failed to cancel job');
    }
  };

  const handleDeleteData = async (dataId) => {
    if (!window.confirm('Delete this training data?')) return;
    
    try {
      await trainingDataAPI.delete(dataId);
      toast.success('Training data deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const copyModelId = (modelId) => {
    navigator.clipboard.writeText(modelId);
    toast.success('Model ID copied');
  };

  const getSelectedKeyVendor = () => {
    const key = apiKeys.find(k => k.id === jobForm.apiKeyId);
    return key?.vendor_code;
  };

  const getVendorModels = () => {
    const vendorCode = getSelectedKeyVendor();
    const vendor = vendors.find(v => v.vendor_code === vendorCode);
    return vendor?.supported_models || [];
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  const validatedData = trainingData.filter(d => d.is_validated && d.file_id);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="project-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold tracking-tight font-heading">{project?.name}</h1>
            {project?.description && (
              <p className="text-muted-foreground mt-1">{project.description}</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="data" data-testid="tab-data">Training Data</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="tab-jobs">Jobs</TabsTrigger>
            <TabsTrigger value="models" data-testid="tab-models">Models</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Training Data</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{trainingData.length}</div>
                  <p className="text-xs text-muted-foreground">{validatedData.length} validated</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Jobs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{jobs.length}</div>
                  <p className="text-xs text-muted-foreground">
                    {jobs.filter(j => j.status === 'succeeded').length} succeeded
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium">Models</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{models.length}</div>
                  <p className="text-xs text-muted-foreground">Ready for use</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="flex gap-3">
                <Button variant="outline" onClick={() => { setActiveTab('data'); setShowUploadDialog(true); }} data-testid="quick-upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Data
                </Button>
                <Button onClick={() => { setActiveTab('jobs'); setShowJobDialog(true); }} disabled={validatedData.length === 0} data-testid="quick-create-job-btn">
                  <Play className="w-4 h-4 mr-2" />
                  Create Job
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Training Data Tab */}
          <TabsContent value="data" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Training Data</h2>
              <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="upload-data-btn">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Upload Training Data</DialogTitle>
                    <DialogDescription>
                      Upload a JSONL file with chat completions format
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        data-testid="upload-name-input"
                        placeholder="Training data name"
                        value={uploadForm.name}
                        onChange={(e) => setUploadForm({ ...uploadForm, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Key (for vendor)</Label>
                      <Select
                        value={uploadForm.apiKeyId}
                        onValueChange={(v) => setUploadForm({ ...uploadForm, apiKeyId: v })}
                      >
                        <SelectTrigger data-testid="upload-apikey-select">
                          <SelectValue placeholder="Select API key" />
                        </SelectTrigger>
                        <SelectContent>
                          {apiKeys.map((key) => (
                            <SelectItem key={key.id} value={key.id}>
                              {key.name} ({key.vendor_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>JSONL File</Label>
                      <Input
                        type="file"
                        accept=".jsonl"
                        data-testid="upload-file-input"
                        onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files[0] })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Cancel</Button>
                    <Button onClick={handleUpload} disabled={uploading} data-testid="submit-upload-btn">
                      {uploading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Upload
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Samples</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>File ID</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {trainingData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No training data yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    trainingData.map((data) => (
                      <TableRow key={data.id} data-testid={`data-row-${data.id}`}>
                        <TableCell className="font-medium">{data.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{data.vendor_code}</Badge>
                        </TableCell>
                        <TableCell>{data.sample_count}</TableCell>
                        <TableCell>
                          <Badge variant={data.is_validated ? 'success' : 'destructive'}>
                            {data.is_validated ? 'Valid' : 'Invalid'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {data.file_id ? data.file_id.slice(0, 20) + '...' : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteData(data.id)} data-testid={`delete-data-${data.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Fine-tuning Jobs</h2>
              <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
                <DialogTrigger asChild>
                  <Button disabled={validatedData.length === 0 || apiKeys.length === 0} data-testid="create-job-btn">
                    <Play className="w-4 h-4 mr-2" />
                    Create Job
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create Fine-tuning Job</DialogTitle>
                    <DialogDescription>
                      Start a new fine-tuning job with your training data
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Job Name (Optional)</Label>
                      <Input
                        data-testid="job-name-input"
                        placeholder="My fine-tuning job"
                        value={jobForm.name}
                        onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>API Key</Label>
                      <Select
                        value={jobForm.apiKeyId}
                        onValueChange={(v) => setJobForm({ ...jobForm, apiKeyId: v, baseModel: '' })}
                      >
                        <SelectTrigger data-testid="job-apikey-select">
                          <SelectValue placeholder="Select API key" />
                        </SelectTrigger>
                        <SelectContent>
                          {apiKeys.map((key) => (
                            <SelectItem key={key.id} value={key.id}>
                              {key.name} ({key.vendor_code})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Training Data</Label>
                      <Select
                        value={jobForm.trainingDataId}
                        onValueChange={(v) => setJobForm({ ...jobForm, trainingDataId: v })}
                      >
                        <SelectTrigger data-testid="job-data-select">
                          <SelectValue placeholder="Select training data" />
                        </SelectTrigger>
                        <SelectContent>
                          {validatedData.filter(d => {
                            const keyVendor = getSelectedKeyVendor();
                            return !keyVendor || d.vendor_code === keyVendor;
                          }).map((data) => (
                            <SelectItem key={data.id} value={data.id}>
                              {data.name} ({data.sample_count} samples)
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Base Model</Label>
                      <Select
                        value={jobForm.baseModel}
                        onValueChange={(v) => setJobForm({ ...jobForm, baseModel: v })}
                        disabled={!jobForm.apiKeyId}
                      >
                        <SelectTrigger data-testid="job-model-select">
                          <SelectValue placeholder="Select base model" />
                        </SelectTrigger>
                        <SelectContent>
                          {getVendorModels().map((model) => (
                            <SelectItem key={model} value={model}>{model}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Epochs</Label>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        data-testid="job-epochs-input"
                        value={jobForm.nEpochs}
                        onChange={(e) => setJobForm({ ...jobForm, nEpochs: parseInt(e.target.value) || 3 })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowJobDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateJob} disabled={creatingJob} data-testid="submit-job-btn">
                      {creatingJob && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Create Job
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name / Model</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Fine-tuned Model</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {jobs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No jobs yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    jobs.map((job) => (
                      <TableRow key={job.id} data-testid={`job-row-${job.id}`}>
                        <TableCell>
                          <div className="font-medium">{job.name || 'Unnamed'}</div>
                          <div className="text-xs text-muted-foreground">{job.base_model}</div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{job.vendor_code}</Badge>
                        </TableCell>
                        <TableCell className="min-w-[150px]">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {statusIcons[job.status]}
                              <Badge variant={statusColors[job.status]}>{job.status}</Badge>
                            </div>
                            <Progress value={progressValues[job.status] || 0} className="h-1.5" />
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs max-w-[200px] truncate">
                          {job.fine_tuned_model || '-'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(job.created_at).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => { setSelectedJobId(job.id); setShowJobDetailDialog(true); }}
                              data-testid={`view-job-${job.id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {['pending', 'queued', 'running'].includes(job.status) && (
                              <Button variant="ghost" size="sm" onClick={() => handleCancelJob(job.id)} data-testid={`cancel-job-${job.id}`}>
                                Cancel
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </TabsContent>

          {/* Models Tab */}
          <TabsContent value="models" className="space-y-4">
            <h2 className="text-xl font-semibold">Fine-tuned Models</h2>

            {models.length === 0 ? (
              <Card className="py-12">
                <CardContent className="text-center">
                  <Cpu className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No models yet</h3>
                  <p className="text-muted-foreground">
                    Complete a fine-tuning job to get your first model
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {models.map((model) => (
                  <Card key={model.id} data-testid={`model-card-${model.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-base">{model.name || 'Unnamed Model'}</CardTitle>
                          <CardDescription>{model.base_model}</CardDescription>
                        </div>
                        <Badge variant="outline" className="capitalize">{model.vendor_code}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Model ID:</span>
                        <code className="flex-1 text-xs bg-muted px-2 py-1 rounded truncate">
                          {model.model_id}
                        </code>
                        <Button variant="ghost" size="icon" onClick={() => copyModelId(model.model_id)} data-testid={`copy-model-${model.id}`}>
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" asChild data-testid={`playground-model-${model.id}`}>
                          <a href={`/playground?model=${model.model_id}&vendor=${model.vendor_code}`}>
                            <Play className="w-4 h-4 mr-1" />
                            Test in Playground
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Job Detail Dialog */}
        <JobDetailDialog
          jobId={selectedJobId}
          open={showJobDetailDialog}
          onOpenChange={setShowJobDetailDialog}
          onStatusChange={(updatedJob) => {
            setJobs(jobs.map(j => j.id === updatedJob.id ? { ...j, ...updatedJob } : j));
            if (updatedJob.status === 'succeeded') {
              loadData(); // Refresh to get new model
            }
          }}
        />
      </div>
    </DashboardLayout>
  );
}
